import type { Material } from '@cutr/constants/cutlist';
import type {
  NestingConfig,
  NestingInput,
  NestingOutput,
  NestingProgress,
  PartFitErrorData,
} from '@cutr/nesting';

import { api } from '@/api/backend';
import { getMaterial } from '@/api/materials';
import { getCurrentFeatures } from '@/theme';
import type { WorkerMessage } from '@/workers/types';

import {
  buildCutlistGroupForCandidate,
  convertGroupToInput,
  convertGroupToInputForMaterial,
  type NestingGroup,
  partFitsMaterial,
} from './nesting';
import { getNestingDimensions } from './smallPartEdgebanding';

export type OrchestratorCallbacks = {
  beforeRun: () => void;
  onWorkerStart: (
    groupId: string,
    workerIndex: number,
    sheetSize: { lengthMM: number; widthMM: number }
  ) => void;
  onOutput: (
    groupId: string,
    output: NestingOutput,
    articleCode?: string
  ) => void;
  onProgress: (
    groupId: string,
    workerIndex: number,
    totalWorkers: number,
    progress: NestingProgress
  ) => void;
  onError: (message: string, data?: PartFitErrorData) => void;
};

type Candidate = {
  articleCode: string;
  nestingInput: NestingInput;
};

type ActiveGroup = {
  abort: () => void;
};

export function resolveCandidates(nestingGroup: NestingGroup): Candidate[] {
  const { materialGroup, smallPartEdgebandingLimits } = nestingGroup;
  const parts = nestingGroup.parts.map((part) => ({
    ...part,
    ...getNestingDimensions(part, smallPartEdgebandingLimits),
  }));

  if (materialGroup.sheetSizeSelection !== 'automatic') {
    return [
      {
        articleCode: materialGroup.core1 ?? '',
        nestingInput: convertGroupToInput(nestingGroup),
      },
    ];
  }

  const seen = new Set<string>();
  const candidates: Candidate[] = [];

  for (const articleCode of materialGroup.automaticSheetSizeMaterials) {
    const material = getMaterial(articleCode);
    if (!material) continue;

    const key = `${material.lengthMM}x${material.widthMM}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const allPartsFit = parts.every(
      (part) =>
        partFitsMaterial(part, 'horizontal', material) ||
        partFitsMaterial(part, 'vertical', material)
    );
    if (!allPartsFit) continue;

    candidates.push({
      articleCode,
      nestingInput: convertGroupToInputForMaterial(
        nestingGroup,
        material as Material
      ),
    });
  }

  if (candidates.length === 0) {
    const lastCode = materialGroup.automaticSheetSizeMaterials.at(-1);
    if (lastCode) {
      const material = getMaterial(lastCode);
      return [
        {
          articleCode: lastCode,
          nestingInput: material
            ? convertGroupToInputForMaterial(nestingGroup, material as Material)
            : convertGroupToInput(nestingGroup),
        },
      ];
    }
    return [
      {
        articleCode: materialGroup.core1 ?? '',
        nestingInput: convertGroupToInput(nestingGroup),
      },
    ];
  }

  return candidates;
}

export function NestingOrchestrator({
  nestingConfig,
  cutlistId,
}: {
  nestingConfig: NestingConfig;
  cutlistId: string;
}) {
  const features = getCurrentFeatures();
  const nestingConfigWithFeatures = {
    ...nestingConfig,
    stripSafetyMargins: features.stripSafetyMargins,
    doubleCountAllSidesSheetEdgeTrim:
      features.doubleCountAllSidesSheetEdgeTrim ?? true,
  };

  const activeWorkers = new Map<string, ActiveGroup>();

  function createWorker() {
    return new Worker(
      new URL('../workers/nesting.worker.ts', import.meta.url),
      { type: 'module' }
    );
  }

  return {
    runGroup(
      groupId: string,
      nestingGroup: NestingGroup,
      callbacks: OrchestratorCallbacks
    ) {
      const existing = activeWorkers.get(groupId);
      if (existing) {
        existing.abort();
        activeWorkers.delete(groupId);
      }

      callbacks.beforeRun();
      const candidates = resolveCandidates(nestingGroup);
      const totalWorkers = candidates.length;
      const results: Array<{ articleCode: string; output: NestingOutput }> = [];
      const errors: Array<{ message: string; data?: PartFitErrorData }> = [];
      const lastProgress: Record<number, NestingProgress> = {};
      let completedCount = 0;
      // Flipped to true when this run is aborted or superseded by another
      // runGroup. Guards async work in finalize() so a late-arriving pricing
      // response from an abandoned run doesn't fire callbacks.
      let superseded = false;

      function handleWorkerDone(worker: Worker) {
        completedCount++;
        worker.terminate();
        if (completedCount === totalWorkers) {
          void finalize();
        }
      }

      function handleMessage(
        candidate: Candidate,
        workerIndex: number,
        worker: Worker,
        msg: WorkerMessage
      ) {
        switch (msg.type) {
          case 'progress':
            lastProgress[workerIndex] = msg.progress;
            callbacks.onProgress(
              groupId,
              workerIndex,
              totalWorkers,
              msg.progress
            );
            break;
          case 'intermediate-result':
            callbacks.onOutput(groupId, msg.output, candidate.articleCode);
            break;
          case 'done':
            results.push({
              articleCode: candidate.articleCode,
              output: msg.output,
            });
            callbacks.onProgress(groupId, workerIndex, totalWorkers, {
              ...lastProgress[workerIndex],
              value: 1,
            });
            handleWorkerDone(worker);
            break;
          case 'error':
            errors.push({ message: msg.message, data: msg.data });
            handleWorkerDone(worker);
            break;
        }
      }

      function spawnWorker(candidate: Candidate, workerIndex: number) {
        const worker = createWorker();
        const { lengthMM, widthMM } = candidate.nestingInput.sheetConfig;
        callbacks.onWorkerStart(groupId, workerIndex, { lengthMM, widthMM });

        worker.onmessage = (event: MessageEvent<WorkerMessage>) =>
          handleMessage(candidate, workerIndex, worker, event.data);

        worker.onerror = (event: ErrorEvent) => {
          event.preventDefault();
          errors.push({
            message: event.message || 'Worker failed unexpectedly',
          });
          handleWorkerDone(worker);
        };

        worker.onmessageerror = () => {
          errors.push({ message: 'Worker message could not be deserialized' });
          handleWorkerDone(worker);
        };

        worker.postMessage({
          nestingInput: candidate.nestingInput,
          nestingConfig: nestingConfigWithFeatures,
          emitIntermediateResults: features.sheetSizeSelection === 'manual',
        });

        return worker;
      }

      const workers = candidates.map((candidate, i) =>
        spawnWorker(candidate, i)
      );

      const me: ActiveGroup = {
        abort: () => {
          superseded = true;
          workers.forEach((w) => w.terminate());
        },
      };
      activeWorkers.set(groupId, me);

      async function finalize() {
        try {
          if (superseded) return;

          if (results.length === 0) {
            if (errors.length > 0) {
              const lastError = errors[errors.length - 1];
              callbacks.onError(lastError.message, lastError.data);
            }
            return;
          }

          if (results.length === 1) {
            const only = results[0];
            callbacks.onOutput(groupId, only.output, only.articleCode);
            return;
          }

          let response;
          try {
            const apiCandidates = results.map((r) => ({
              articleCode: r.articleCode,
              cutlistGroup: buildCutlistGroupForCandidate(nestingGroup, r),
            }));
            response = await api.nestingBatchPrice(cutlistId, apiCandidates);
          } catch (err) {
            if (superseded) return;
            const message =
              err instanceof Error ? err.message : 'Pricing request failed';
            callbacks.onError(message);
            return;
          }

          if (superseded) return;

          const winner = results.find(
            (r) => r.articleCode === response.winningArticleCode
          );
          if (!winner) {
            callbacks.onError('Pricing returned unknown winning SKU');
            return;
          }
          callbacks.onOutput(groupId, winner.output, winner.articleCode);
        } finally {
          if (activeWorkers.get(groupId) === me) {
            activeWorkers.delete(groupId);
          }
        }
      }
    },

    abortGroup(groupId: string) {
      const group = activeWorkers.get(groupId);
      if (group) {
        group.abort();
        activeWorkers.delete(groupId);
      }
    },

    abortAll() {
      activeWorkers.forEach(({ abort }) => abort());
      activeWorkers.clear();
    },
  };
}
